/**
 * <FearGreed> — CNN 공포·탐욕 지수
 *
 * 레이아웃 (플랫 · Bloomberg 풍):
 *   [큰 숫자 뱃지]  지금 · 탐욕
 *   [━━━━●━━━━] 0~100 가로 그라데이션 bar + 현재 위치 포인터
 *   0  25  50  75  100
 *
 *   ── 최근 추이 ──
 *   어제   지난주   지난달
 *    70     49      16
 *    ▲      ▼       ▼
 *
 * 데이터: GET /api/ticker/market/fear-greed
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface FearGreedData {
  updatedAt: string;
  score: number;
  rating: string;
  ratingKo: string;
  previousClose: number | null;
  previousWeek: number | null;
  previousMonth: number | null;
  previousYear: number | null;
  timestamp: string | null;
}

const REFRESH_MS = 10 * 60 * 1000;

/**
 * 점수 → 글자/강조 색상 (풀 톤만 사용 · soft 톤은 반투명처럼 보여서 금지)
 * 3단계로 단순화: 공포(down) · 중립(ink) · 탐욕(up)
 */
function colorForScore(s: number): string {
  if (s <= 45) return "var(--bs-down)";
  if (s <= 55) return "var(--bs-gray-700)";
  return "var(--bs-up)";
}

/**
 * 숫자 뱃지 배경 — 현재 점수에 따라 down/up 을 옅게 깐다.
 * intensity 는 최대 38% 까지만 → 배경-글자 대비 확보
 */
function badgeBackground(s: number): string {
  if (s >= 45 && s <= 55) return "var(--bs-gray-100)";
  const base = s < 45 ? "var(--bs-down)" : "var(--bs-up)";
  const distance = s < 45 ? (45 - s) / 45 : (s - 55) / 45;
  const intensity = Math.round(14 + distance * 24); // 14%~38%
  return `color-mix(in srgb, ${base} ${intensity}%, var(--bs-paper))`;
}

/** 과거 점수 비교 한 컬럼 */
function PastItem({
  label,
  value,
  current,
}: {
  label: string;
  value: number | null;
  current: number;
}) {
  if (value == null) {
    return (
      <div className="dw-fg-past">
        <span className="dw-fg-past-label">{label}</span>
        <span className="dw-fg-past-value">—</span>
      </div>
    );
  }
  const diff = current - value;
  const up = diff > 0.5;
  const down = diff < -0.5;
  const color = colorForScore(value);
  return (
    <div className="dw-fg-past">
      <span className="dw-fg-past-label">{label}</span>
      <span className="dw-fg-past-value" style={{ color }}>
        {Math.round(value)}
      </span>
      <span className={`dw-fg-past-diff ${up ? "up" : down ? "down" : ""}`}>
        {up ? "▲" : down ? "▼" : "—"} {Math.abs(diff).toFixed(0)}
      </span>
    </div>
  );
}

export function FearGreed(_props: WidgetProps) {
  const [data, setData] = useState<FearGreedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/fear-greed", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) {
            setError("로드 실패");
            setLoading(false);
          }
          return;
        }
        const d = (await res.json()) as FearGreedData;
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) {
          setError("네트워크 오류");
          setLoading(false);
        }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, []);

  const score = data?.score ?? 50;
  const scoreColor = colorForScore(score);
  const badgeBg = badgeBackground(score);

  if (error) {
    return (
      <div className="dw-body dw-fg">
        <div className="dw-head">
          <span className="dw-title">공포·탐욕 지수</span>
        </div>
        <div className="dw-fg-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="dw-body dw-fg">
      <div className="dw-head">
        <span className="dw-title">공포·탐욕 지수</span>
      </div>

      {/* 대표 숫자 뱃지 + rating */}
      <div className="dw-fg-hero">
        <div
          className="dw-fg-badge"
          style={{ background: badgeBg, color: scoreColor }}
        >
          {loading && !data ? (
            <span className="bs-skel" style={{ width: 40, height: 28, display: "block" }} />
          ) : (
            <span className="dw-fg-badge-num">{Math.round(score)}</span>
          )}
        </div>
        <div className="dw-fg-meta">
          <span className="dw-fg-meta-kicker">지금</span>
          <span className="dw-fg-meta-rating" style={{ color: scoreColor }}>
            {loading && !data ? "…" : data?.ratingKo || "—"}
          </span>
        </div>
      </div>

      {/* 가로 그라데이션 bar + 포인터 */}
      <div className="dw-fg-bar">
        <div className="dw-fg-bar-track" />
        <div
          className="dw-fg-bar-pointer"
          style={{ left: `${Math.max(0, Math.min(100, score))}%` }}
          aria-hidden="true"
        />
        <div className="dw-fg-bar-scale">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </div>
      </div>

      {/* 과거 추이 */}
      <div className="dw-fg-history">
        <div className="dw-fg-history-label">최근 추이</div>
        <div className="dw-fg-history-grid">
          <PastItem label="어제" value={data?.previousClose ?? null} current={score} />
          <PastItem label="지난주" value={data?.previousWeek ?? null} current={score} />
          <PastItem label="지난달" value={data?.previousMonth ?? null} current={score} />
        </div>
      </div>
    </div>
  );
}

export const fearGreedMeta: WidgetMeta = {
  type: "fear-greed",
  name: "공포·탐욕 지수",
  description: "CNN Fear & Greed — 시장 심리 0~100",
  icon: "🌡️",
  category: "market",
  defaultSize: { w: 4, h: 4 },
  availableSizes: [
    { w: 3, h: 4 },
    { w: 4, h: 4 },
    { w: 4, h: 5 },
  ],
  minW: 3,
  maxW: 6,
  minH: 3,
  maxH: 6,
  component: FearGreed,
};
